import { FaBook, FaNewspaper, FaWallet, FaUser, FaCog, FaChevronDown, FaShieldAlt, FaHome, FaBell, FaAngleDoubleLeft, FaChartBar } from 'react-icons/fa';
import { FaWhatsapp } from 'react-icons/fa';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Sidebar = React.forwardRef(({ isMenuOpen, isCollapsed, onToggleCollapse }, ref) => {
  const { hasPermission, user } = useAuth();
  const [configOpen, setConfigOpen] = useState(false);
  const [pessoasOpen, setPessoasOpen] = useState(false);
  const [pedidosOpen, setPedidosOpen] = useState(false);
  const [academicoOpen, setAcademicoOpen] = useState(false);
  const [estoqueOpen, setEstoqueOpen] = useState(false);
  const [financeiroOpen, setFinanceiroOpen] = useState(false);
  const [segurancaOpen, setSegurancaOpen] = useState(false);
  const [relatoriosOpen, setRelatoriosOpen] = useState(false);

  // Soma de permissões de cada relatório individual — quando UMA delas
  // existir o menu "Relatórios" deve aparecer. Adicionar novos relatórios
  // protegidos no array abaixo.
  const PERMISSOES_RELATORIOS = [
    'relatorios.extensoes_sem_pedidos.view',
  ];
  const podeVerMenuRelatorios = hasPermission('relatorios.menu.access')
    || PERMISSOES_RELATORIOS.some(p => hasPermission(p));

  // Verificar se o usuário pode ver notificações (operadores nível >= 4 OU coordenadores nível 2-3)
  // Grupos do menu só aparecem se houver ao menos um item utilizável dentro
  // deles — um link que sempre leva a "Acesso negado" é um beco sem saída.
  const podeVerMenuPessoas = hasPermission('alunos.list')
    || hasPermission('funcionarios.list')
    || hasPermission('colaboradores.list');

  const podeVerMenuPedidos = hasPermission('pedidos.list')
    || hasPermission('pedidos.approve_cancellation');

  const nivelAcesso = user?.nivel_acesso || user?.nivelAcesso || user?.perfil?.nivelAcesso;
  const podeVerNotificacoes = nivelAcesso >= 2;

  return (
    <aside
      ref={ref}
      className={`transition-all duration-300 ${
        isMenuOpen 
          ? 'block opacity-100 translate-x-0' 
          : 'hidden lg:block opacity-100'
      } ${
        isCollapsed ? 'lg:-translate-x-full lg:w-0 lg:overflow-hidden' : 'lg:translate-x-0 lg:w-64'
      } w-64 bg-white shadow-md fixed lg:relative z-30 top-0 left-0 h-screen lg:h-auto overflow-y-auto`}
    >
      {/* Botão de toggle - posicionado absolutamente no canto */}
      <button
        onClick={onToggleCollapse}
        className="hidden lg:flex absolute top-2 right-2 z-10 items-center justify-center w-7 h-7 rounded-md hover:bg-gray-100 transition-colors duration-200 group"
        title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
      >
        <FaAngleDoubleLeft className="text-gray-400 group-hover:text-indigo-600 transition-colors text-sm" />
      </button>
      <nav className="p-4">
        <ul className="space-y-2">
          {/* Dashboard */}
          <li>
            <Link to="/" className="flex items-center p-2 rounded hover:bg-gray-100">
              <FaHome className="text-indigo-600" />
              <span className="ml-2 font-semibold">Dashboard</span>
            </Link>
          </li>

          {/* Notificações - para operadores (nível >= 4) e coordenadores (nível 2-3) */}
          {podeVerNotificacoes && (
            <li>
              <Link to="/notificacoes" className="flex items-center p-2 rounded hover:bg-gray-100">
                <FaBell className="text-red-500" />
                <span className="ml-2 font-semibold">Notificações</span>
              </Link>
            </li>
          )}
          
          {/* Agendamentos WhatsApp - para administradores (nível >= 5) */}
          {nivelAcesso >= 5 && (
            <li>
              <Link to="/agendamentos-whatsapp" className="flex items-center p-2 rounded hover:bg-gray-100">
                <FaWhatsapp className="text-green-500" />
                <span className="ml-2 font-semibold">Agendamentos WhatsApp</span>
              </Link>
            </li>
          )}
          
          {/* O grupo Pessoas era o único sempre visível: quem não tinha as
              permissões via os três links e caía em "Acesso negado" a cada
              clique. Agora segue o mesmo critério dos demais grupos. */}
          {podeVerMenuPessoas && (
          <li>
            <button type="button" className="flex items-center w-full p-2 rounded hover:bg-gray-100 focus:outline-none" onClick={() => setPessoasOpen(!pessoasOpen)}>
              <FaUser className="text-purple-600" />
              <span className="ml-2 flex-1 text-left font-semibold">Pessoas</span>
              <FaChevronDown className={`ml-2 transition-transform duration-200 ${pessoasOpen ? 'rotate-180' : ''}`} />
            </button>
            {pessoasOpen && (
              <ul className="ml-8 mt-2 space-y-1">
                {hasPermission('alunos.list') && (
                  <li>
                    <Link to="/alunos" className="block p-2 rounded hover:bg-gray-100">Alunos</Link>
                  </li>
                )}
                {hasPermission('funcionarios.list') && (
                  <li>
                    <Link to="/funcionarios" className="block p-2 rounded hover:bg-gray-100">Funcionários</Link>
                  </li>
                )}
                {hasPermission('colaboradores.list') && (
                  <li>
                    <Link to="/colaboradores" className="block p-2 rounded hover:bg-gray-100">Colaboradores</Link>
                  </li>
                )}
                {/* Link para documentos pendentes - apenas para nível > 3 */}
                {(user?.nivel_acesso > 3 || user?.nivelAcesso > 3 || user?.perfil?.nivelAcesso > 3) && (
                  <li>
                    <Link to="/alunos/documentos-pendentes" className="block p-2 rounded hover:bg-gray-100">Documentos Pendentes</Link>
                  </li>
                )}
              </ul>
            )}
          </li>
          )}
          <li>
            <Link to="/mural" className="flex items-center p-2 rounded hover:bg-gray-100">
              <FaNewspaper className="text-gray-600" />
              <span className="ml-2">Mural de Notícias</span>
            </Link>
          </li>
          
          {/* Menu Relatórios - aparece se o usuário tem permissão para ao menos um relatório */}
          {podeVerMenuRelatorios && (
            <li>
              <button type="button" className="flex items-center w-full p-2 rounded hover:bg-gray-100 focus:outline-none" onClick={() => setRelatoriosOpen(!relatoriosOpen)}>
                <FaChartBar className="text-indigo-600" />
                <span className="ml-2 flex-1 text-left font-semibold">Relatórios</span>
                <FaChevronDown className={`ml-2 transition-transform duration-200 ${relatoriosOpen ? 'rotate-180' : ''}`} />
              </button>
              {relatoriosOpen && (
                <ul className="ml-8 mt-2 space-y-1">
                  {hasPermission('relatorios.extensoes_sem_pedidos.view') && (
                    <li>
                      <Link to="/relatorios/extensoes-sem-pedidos" className="block p-2 rounded hover:bg-gray-100">
                        Extensões sem pedidos
                      </Link>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Menu Financeiro - só aparece se tiver permissão em pelo menos um submenu */}
          {(hasPermission('financeiro.receitas.list') || hasPermission('financeiro.despesas.list') || hasPermission('financeiro.contas_pagar.list')) && (
            <li>
              <button type="button" className="flex items-center w-full p-2 rounded hover:bg-gray-100 focus:outline-none" onClick={() => setFinanceiroOpen(!financeiroOpen)}>
                <FaWallet className="text-green-600" />
                <span className="ml-2 flex-1 text-left font-semibold">Financeiro</span>
                <FaChevronDown className={`ml-2 transition-transform duration-200 ${financeiroOpen ? 'rotate-180' : ''}`} />
              </button>
              {financeiroOpen && (
                <ul className="ml-8 mt-2 space-y-1">
                  {hasPermission('financeiro.receitas.list') && (
                    <li>
                      <Link to="/financeiro/receitas" className="block p-2 rounded hover:bg-gray-100">Receitas</Link>
                    </li>
                  )}
                  {hasPermission('financeiro.despesas.list') && (
                    <li>
                      <Link to="/financeiro/despesas" className="block p-2 rounded hover:bg-gray-100">Despesas</Link>
                    </li>
                  )}
                  {hasPermission('financeiro.contas_pagar.list') && (
                    <li>
                      <Link to="/financeiro/contas-pagar" className="block p-2 rounded hover:bg-gray-100">Contas a Pagar</Link>
                    </li>
                  )}
                </ul>
              )}
            </li>
          )}
          <li>
            {/* Menu Pedidos - expansível (só para quem tem alguma permissão nele) */}
            {podeVerMenuPedidos && (
            <>
            <button type="button" className="flex items-center w-full p-2 rounded hover:bg-gray-100 focus:outline-none" onClick={() => setPedidosOpen(!pedidosOpen)}>
              <FaNewspaper className="text-orange-600" />
              <span className="ml-2 flex-1 text-left font-semibold">Pedidos</span>
              <FaChevronDown className={`ml-2 transition-transform duration-200 ${pedidosOpen ? 'rotate-180' : ''}`} />
            </button>
            {pedidosOpen && (
              <ul className="ml-8 mt-2 space-y-1">
                {hasPermission('pedidos.list') && (
                  <li>
                    <Link to="/pedidos" className="block p-2 rounded hover:bg-gray-100">Lista de Pedidos</Link>
                  </li>
                )}
                {hasPermission('pedidos.approve_cancellation') && (
                  <li>
                    <Link to="/pedidos/cancelamentos" className="block p-2 rounded hover:bg-gray-100">Gerenciar Cancelamentos</Link>
                  </li>
                )}
              </ul>
            )}
            </>
            )}
          </li>
          <li>
            {/* Menu Acadêmico - padrão expansível igual Pessoas */}
            <button type="button" className="flex items-center w-full p-2 rounded hover:bg-gray-100 focus:outline-none" onClick={() => setAcademicoOpen && setAcademicoOpen(prev => !prev)}>
              <FaBook className="text-blue-600" />
              <span className="ml-2 flex-1 text-left font-semibold">Acadêmico</span>
              <FaChevronDown className={`ml-2 transition-transform duration-200 ${academicoOpen ? 'rotate-180' : ''}`} />
            </button>
            {academicoOpen && (
              <ul className="ml-8 mt-2 space-y-1">
                {hasPermission('matriculas.list') && (
                  <li>
                    <Link to="/matriculas" className="block p-2 rounded hover:bg-gray-100">Matrículas</Link>
                  </li>
                )}
                {hasPermission('matriculas.read') && (
                  <li>
                    <Link to="/matriculas/credenciais" className="block p-2 rounded hover:bg-gray-100">Imprimir Credenciais</Link>
                  </li>
                )}
                {hasPermission('matriz.list') && (
                  <li>
                    <Link to="/academico/matriz-curricular" className="block p-2 rounded hover:bg-gray-100">Matrizes Curriculares</Link>
                  </li>
                )}
                {hasPermission('academico.notas.lancamento_individual.create') && (
                  <li>
                    <Link to="/academico/lancamento-notas-individual" className="block p-2 rounded hover:bg-gray-100">Lançamento de Notas</Link>
                  </li>
                )}
                {hasPermission('academico.notas.read') && (
                  <li>
                    <Link to="/academico/historico-notas" className="block p-2 rounded hover:bg-gray-100">Histórico de Notas</Link>
                  </li>
                )}
                {hasPermission('academico.transferencia-notas.access') && (
                  <li>
                    <Link to="/academico/transferencia-notas" className="block p-2 rounded hover:bg-gray-100">Transferência de Notas</Link>
                  </li>
                )}
                {hasPermission('academico.notas.list') && (
                  <li>
                    <Link to="/academico/notas" className="block p-2 rounded hover:bg-gray-100">Notas</Link>
                  </li>
                )}
                {hasPermission('protocolos.list') && (
                  <li>
                    <Link to="/protocolos" className="block p-2 rounded hover:bg-gray-100">Protocolos de Requerimento</Link>
                  </li>
                )}
              </ul>
            )}
          </li>
          
          {/* Menu Configurações - só aparece para nível 4+ e com alguma permissão */}
          {(user?.nivel_acesso >= 4 && (
            hasPermission('cursos.list') || 
            hasPermission('extensoes.list') || 
            hasPermission('tipo_produto.list') || 
            hasPermission('produtos.list') || 
            hasPermission('forma_entrega.list') || 
            hasPermission('prazo_entrega.list') || 
            hasPermission('forma_pagamento.list') || 
            hasPermission('contas.list') ||
            hasPermission('credenciais.config') ||
            hasPermission('noticias.create')
          )) && (
            <li>
              <button type="button" className="flex items-center w-full p-2 rounded hover:bg-gray-100 focus:outline-none" onClick={() => setConfigOpen(!configOpen)}>
                <FaCog className="text-gray-600" />
                <span className="ml-2 flex-1 text-left">Configurações</span>
                <FaChevronDown className={`ml-2 transition-transform duration-200 ${configOpen ? 'rotate-180' : ''}`} />
              </button>
              {configOpen && (
                <ul className="ml-8 mt-2 space-y-1">
                  {hasPermission('noticias.create') && (
                    <li><Link to="/mural/gerenciar" className="block p-2 rounded hover:bg-gray-100">Gerenciar Notícias</Link></li>
                  )}
                  {hasPermission('cursos.list') && (
                    <li><Link to="/cursos" className="block p-2 rounded hover:bg-gray-100">Cursos</Link></li>
                  )}
                  {hasPermission('extensoes.list') && (
                    <li><Link to="/extensoes" className="block p-2 rounded hover:bg-gray-100">Extensões</Link></li>
                  )}
                  {hasPermission('tipo_produto.list') && (
                    <li><Link to="/tipo-produto" className="block p-2 rounded hover:bg-gray-100">Tipo de Produto</Link></li>
                  )}
                  {hasPermission('produtos.list') && (
                    <li><Link to="/produtos" className="block p-2 rounded hover:bg-gray-100">Produtos</Link></li>
                  )}
                  {hasPermission('produtos.precos.listar') && (
                    <li><Link to="/produtos/precos-customizados" className="block p-2 rounded hover:bg-gray-100 pl-6">↳ Preços por Extensão</Link></li>
                  )}
                  {hasPermission('forma_entrega.list') && (
                    <li><Link to="/forma-entrega" className="block p-2 rounded hover:bg-gray-100">Formas de Entrega</Link></li>
                  )}
                  {hasPermission('prazo_entrega.list') && (
                    <li><Link to="/prazo-entrega" className="block p-2 rounded hover:bg-gray-100">Prazo de Entrega</Link></li>
                  )}
                  {hasPermission('forma_pagamento.list') && (
                    <li><Link to="/forma-pagamento" className="block p-2 rounded hover:bg-gray-100">Forma de Pagamento</Link></li>
                  )}
                  {hasPermission('contas.list') && (
                    <li><Link to="/contas" className="block p-2 rounded hover:bg-gray-100">Contas</Link></li>
                  )}
                  {hasPermission('credenciais.config') && (
                    <li><Link to="/configuracoes/credenciais" className="block p-2 rounded hover:bg-gray-100">Template Credenciais</Link></li>
                  )}
                  {user?.nivel_acesso >= 5 && hasPermission('configuracoes.email') && (
                    <li><Link to="/configuracoes/email" className="block p-2 rounded hover:bg-gray-100">Configuração de Email</Link></li>
                  )}
                </ul>
              )}
            </li>
          )}
          
          {/* Menu Estoque - só aparece para nível 4+ e com alguma permissão */}
          {(user?.nivel_acesso >= 4 && (
            hasPermission('estoque.list') || 
            hasPermission('estoque.create') || 
            hasPermission('estoque.update')
          )) && (
            <li>
              <button type="button" className="flex items-center w-full p-2 rounded hover:bg-gray-100 focus:outline-none" onClick={() => setEstoqueOpen(!estoqueOpen)}>
                <FaWallet className="text-blue-600" />
                <span className="ml-2 flex-1 text-left">Estoque</span>
                <FaChevronDown className={`ml-2 transition-transform duration-200 ${estoqueOpen ? 'rotate-180' : ''}`} />
              </button>
              {estoqueOpen && (
                <ul className="ml-8 mt-2 space-y-1">
                  {hasPermission('estoque.list') && (
                    <li><Link to="/estoque/dashboard" className="block p-2 rounded hover:bg-gray-100">Dashboard</Link></li>
                  )}
                  {hasPermission('estoque.list') && (
                    <li><Link to="/estoque/relatorio" className="block p-2 rounded hover:bg-gray-100">Relatório</Link></li>
                  )}
                  {hasPermission('estoque.list') && (
                    <li><Link to="/estoque/entrada" className="block p-2 rounded hover:bg-gray-100">Entrada</Link></li>
                  )}
                </ul>
              )}
            </li>
          )}

          {/* Menu Segurança */}
          {(hasPermission('usuarios.list') || hasPermission('perfis.list') || hasPermission('permissoes.list')) && (
            <li>
              <button type="button" className="flex items-center w-full p-2 rounded hover:bg-gray-100 focus:outline-none" onClick={() => setSegurancaOpen(!segurancaOpen)}>
                <FaShieldAlt className="text-red-600" />
                <span className="ml-2 flex-1 text-left">Segurança</span>
                <FaChevronDown className={`ml-2 transition-transform duration-200 ${segurancaOpen ? 'rotate-180' : ''}`} />
              </button>
              {segurancaOpen && (
                <ul className="ml-8 mt-2 space-y-1">
                  {hasPermission('usuarios.list') && (
                    <li><Link to="/seguranca/usuarios" className="block p-2 rounded hover:bg-gray-100">Usuários</Link></li>
                  )}
                  {hasPermission('usuarios.read') && (
                    <li><Link to="/seguranca/usuarios/extensoes" className="block p-2 rounded hover:bg-gray-100">Acesso às Extensões</Link></li>
                  )}
                  {hasPermission('perfis.list') && (
                    <li><Link to="/seguranca/perfis" className="block p-2 rounded hover:bg-gray-100">Perfis</Link></li>
                  )}
                  {hasPermission('permissoes.list') && (
                    <li><Link to="/seguranca/permissoes" className="block p-2 rounded hover:bg-gray-100">Permissões</Link></li>
                  )}
                </ul>
              )}
            </li>
          )}
        </ul>
      </nav>
    </aside>
  );
});

export default Sidebar;
